import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { track } from "../utils/track";

/**
 * Aviso de «sin páginas suficientes» tras un 402 al subir (19-10-2026).
 *
 * Antes, `/upload` enseñaba el mensaje en crudo del servidor («Insufficient tokens. Required: 14, Available: 11»), en
 * inglés y sin botón de compra: la única cuenta que llegó al muro (facturas, Francia) se fue ahí. Si al usuario le
 * quedan páginas pero el documento necesita más, se dice cuántas faltan y cómo seguir (partir el PDF o conseguir más);
 * si no le queda ninguna, el texto de siempre.
 *
 * Props: `required` y `available` (del 402 de `manual_upload`, pueden faltar), `isFreePlan`, `where` (para el evento).
 */
export default function OutOfCreditsNotice({ required, available, isFreePlan, where }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const req = Number(required);
  const avail = Number(available);
  const partial = Number.isFinite(req) && Number.isFinite(avail) && avail > 0 && req > avail;

  useEffect(() => {
    track("out_of_credits_view", {
      where,
      required: Number.isFinite(req) ? req : undefined,
      available: Number.isFinite(avail) ? avail : undefined,
    });
    // una vista por aparición del aviso
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (target, path) => {
    track("out_of_credits_click", { where, target });
    navigate(path);
  };

  let title;
  let text;
  if (partial) {
    title = t("scannerDetail.out_of_credits_partial_title", { required: req, available: avail });
    text = t("scannerDetail.out_of_credits_partial_text");
  } else if (isFreePlan) {
    title = t("scannerDetail.out_of_credits_title");
    text = t("scannerDetail.out_of_credits_text");
  } else {
    title = t("scannerDetail.out_of_credits_paid_title");
    text = t("scannerDetail.out_of_credits_paid_text");
  }

  return (
    <div
      data-testid="out-of-credits"
      className="p-4 bg-amber-50 text-amber-900 text-sm rounded-xl border border-amber-200"
    >
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{text}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => go("pricing", "/pricing")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold"
        >
          {t("scannerDetail.out_of_credits_cta")}
        </button>
        <button
          type="button"
          onClick={() => go("packs", "/tokens")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-lg font-bold"
        >
          {t("scannerDetail.out_of_credits_pack_cta")}
        </button>
      </div>
    </div>
  );
}
